// ─── Formatters ────────────────────────────────────────────────────────────

function fmt$(value, compact = false, currencyCode = 'USD') {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (isNaN(n)) return '—';
  if (compact && Math.abs(n) >= 1_000_000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: currencyCode,
      notation: 'compact', maximumFractionDigits: 1,
    }).format(n);
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: currencyCode, maximumFractionDigits: 0,
  }).format(n);
}

function fmtRelative(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(d);
}

function scoreMeta(score) {
  const n = Number(score ?? 0);
  if (n >= 75) return { badgeClass: 'dash-badge-green', label: 'Strong',   color: '#16A34A' };
  if (n >= 50) return { badgeClass: 'dash-badge-amber', label: 'Moderate', color: '#D97706' };
  return           { badgeClass: 'dash-badge-red',   label: 'Weak',     color: '#DC2626' };
}

// ─── KPI Card ───────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, loading }) {
  return (
    <div className="dash-kpi-card">
      <div className="dash-kpi-label">{label}</div>
      <div className="dash-kpi-value">{loading ? '…' : value}</div>
      {sub && <div className="dash-kpi-sub">{sub}</div>}
    </div>
  );
}

// ─── Valuation Card ─────────────────────────────────────────────────────────
// DB always stores USD values — currency prop is threaded for forward
// compatibility when the detail endpoint is extended to return currency data.

function ValuationCard({ analysis, loading, currency = 'USD' }) {
  if (loading) {
    return (
      <div className="dash-val-card">
        <div className="dash-val-eyebrow">Estimated Company Value</div>
        <div className="dash-val-amount">Loading…</div>
      </div>
    );
  }
  if (!analysis) return null;

  const { valuation, ebitda, multiple } = analysis;
  const low  = Number(valuation?.low  ?? 0);
  const mid  = Number(valuation?.mid  ?? 0);
  const high = Number(valuation?.high ?? 0);
  const span = high - low || 1;
  const midPct = Math.round(((mid - low) / span) * 100);

  return (
    <div className="dash-val-card">
      <div>
        <div className="dash-val-eyebrow">Estimated Company Value</div>
        <div className="dash-val-amount">{fmt$(valuation?.mid, false, currency)}</div>
        <div className="dash-val-sub">
          Mid-point · {Number(multiple?.mid).toFixed(1)}× EBITDA multiple
        </div>
      </div>

      <div className="dash-range-bar">
        <div className="dash-range-labels">
          <span>
            <span style={{ fontSize: 10, color: 'rgba(147,197,253,0.6)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Low</span>
            {fmt$(valuation?.low, true, currency)}
          </span>
          <span style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 10, color: 'rgba(147,197,253,0.6)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>High</span>
            {fmt$(valuation?.high, true, currency)}
          </span>
        </div>
        <div className="dash-range-track">
          <div className="dash-range-fill" />
          <div className="dash-range-marker" style={{ left: `${midPct}%` }} />
        </div>
      </div>

      <div className="dash-val-footer">
        {[
          { label: 'EBITDA',         value: fmt$(ebitda, true, currency),               color: Number(ebitda) >= 0 ? '#4ADE80' : '#F87171' },
          { label: 'Multiple — Low', value: `${Number(multiple?.low).toFixed(1)}×`,     color: '#fff' },
          { label: 'Multiple — High',value: `${Number(multiple?.high).toFixed(1)}×`,    color: '#fff' },
        ].map(({ label, value, color }) => (
          <div key={label}>
            <div className="dash-val-metric-label">{label}</div>
            <div className="dash-val-metric-value" style={{ color }}>{value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Readiness Gauge ────────────────────────────────────────────────────────

function ReadinessPanel({ score, loading }) {
  const n          = Number(score ?? 0);
  const meta       = scoreMeta(n);
  const circumference = Math.PI * 54;
  const offset     = circumference * (1 - n / 100);

  return (
    <div className="dash-panel">
      <div className="dash-panel-header">Sellability Index</div>
      <div className="dash-panel-body">
        {loading ? (
          <div style={{ color: '#9CA3AF', fontSize: 14 }}>Loading…</div>
        ) : (
          <>
            <div style={{ position: 'relative', width: 160, height: 96 }}>
              <svg viewBox="0 0 128 72" style={{ width: '100%', overflow: 'visible' }}>
                <path d="M 10 64 A 54 54 0 0 1 118 64"
                  fill="none" stroke="#E2E8F0" strokeWidth="8" strokeLinecap="round" />
                <path d="M 10 64 A 54 54 0 0 1 118 64"
                  fill="none" stroke={meta.color} strokeWidth="8" strokeLinecap="round"
                  strokeDasharray={circumference} strokeDashoffset={offset}
                  style={{ transition: 'stroke-dashoffset 0.8s ease' }}
                />
              </svg>
              <div style={{
                position: 'absolute', bottom: 0, left: '50%',
                transform: 'translateX(-50%)', textAlign: 'center',
              }}>
                <div className="dash-score-number">{n.toFixed(0)}</div>
                <div className="dash-score-sub">/ 100</div>
              </div>
            </div>

            <span className={`dash-badge ${meta.badgeClass}`}>
              {meta.label} Sellability
            </span>

            <div className="dash-score-rows">
              <div className="dash-score-row">
                <span>Score</span>
                <span>{n.toFixed(2)}</span>
              </div>
              <div className="dash-score-row">
                <span>Band</span>
                <span style={{ color: meta.color }}>{meta.label}</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Page Root ──────────────────────────────────────────────────────────────

export default function ValuationDashboard({
  latest        = null,
  loading       = false,
  currency      = 'USD',
  onNewAnalysis,
}) {
  const revenue   = latest?.input_snapshot?.gross_annual_revenue ?? null;
  const ebitda    = latest?.ebitda ?? null;
  const score     = latest?.score ?? latest?.readiness_score ?? null;
  const ebitdaNum = Number(ebitda ?? 0);
  const revNum    = Number(revenue ?? 0);
  const margin    = revNum > 0 ? ((ebitdaNum / revNum) * 100).toFixed(1) + '%' : null;
  const hasData   = Boolean(latest);

  return (
    <div className="dash-page">

      {/* Header */}
      <div className="dash-header">
        <div className="dash-header-info">
          <h1>Valuation Dashboard</h1>
          <p>
            {loading ? 'Loading…'
              : hasData ? `Last analysis · ${fmtRelative(latest.created_at)}`
              : 'No analyses yet — run your first valuation'}
          </p>
        </div>
        <div className="dash-header-actions">
          <button className="dash-btn-secondary" onClick={() => window.print()}>
            Export PDF
          </button>
          <button className="dash-btn-primary" onClick={onNewAnalysis}>
            + New Analysis
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="dash-content">

        {/* KPI strip */}
        <div className="dash-kpi-strip">
          <KpiCard
            label="Gross Revenue"
            value={fmt$(revenue, false, currency)}
            sub="Annual"
            loading={loading}
          />
          <KpiCard
            label="EBITDA"
            value={fmt$(ebitda, false, currency)}
            sub={margin ? `${margin} margin` : undefined}
            loading={loading}
          />
          <KpiCard
            label="Sellability Index"
            value={score != null ? `${Number(score).toFixed(1)} / 100` : '—'}
            sub={hasData ? `${scoreMeta(score).label} band` : 'out of 100'}
            loading={loading}
          />
        </div>

        {/* Valuation + Readiness */}
        {(hasData || loading) && (
          <div className="dash-main-row">
            <ValuationCard analysis={latest} loading={loading} currency={currency} />
            <ReadinessPanel score={score} loading={loading} />
          </div>
        )}

        {/* Empty state */}
        {!hasData && !loading && (
          <div className="dash-table-card">
            <div className="dash-empty">
              <div className="dash-empty-icon">📈</div>
              <p>No valuation data</p>
              <small>Run your first valuation to see your Sellability Index and company value here.</small>
              <div style={{ marginTop: 16 }}>
                <button className="dash-btn-primary" onClick={onNewAnalysis}>
                  Run First Analysis
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
